import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { RUNS } from './geometry';
import { formatDate } from './format';
import GovernedEditForm from './GovernedEditForm';
import useWarehouseEditAccess from './useWarehouseEditAccess';
import { CAPACITY_PROFILE_FIELDS, LOCATION_GROUP_FIELDS } from './governedEdit';
import useLocationCapacitySheetSyncAccess from './useLocationCapacitySheetSyncAccess';
import CapacitySheetSyncModal from './CapacitySheetSyncModal';

const TABS = [
  { key: 'locations', label: 'Locations' },
  { key: 'groups', label: 'Groups' },
  { key: 'capacity-profiles', label: 'Capacity profiles' },
  { key: 'capacity-table', label: 'Capacity table' },
  { key: 'changes', label: 'Changes' },
];
const AVAILABILITY = ['Available', 'Blocked', 'Temporarily Unavailable', 'Reserved', 'Requires Validation'];

const LocationMasterPanel = ({ initialTab = 'locations', initialArea, initialQuery, onClose, onSelectLocation, onSaved }) => {
  const canEdit = useWarehouseEditAccess();
  const capacitySheetSyncAccess = useLocationCapacitySheetSyncAccess();
  const [tab, setTab] = useState(initialTab);
  const [area, setArea] = useState(initialArea || '');
  const [availability, setAvailability] = useState('');
  const [group, setGroup] = useState('');
  const [query, setQuery] = useState(initialQuery || '');
  const [page, setPage] = useState(1);
  const [locations, setLocations] = useState({ rows: [], total: 0, summary: {} });
  const [groups, setGroups] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [capacityMatrix, setCapacityMatrix] = useState([]);
  const [capacityQuery, setCapacityQuery] = useState('');
  const [syncOpen, setSyncOpen] = useState(false);
  const [changes, setChanges] = useState({ rows: [], count: 0 });
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const locationGroups = useMemo(() => groups.map((row) => ({ name: row.name })), [groups]);
  const options = useMemo(() => ({ locationGroups, capacityProfiles: profiles }), [locationGroups, profiles]);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '50' });
      if (area) params.set('area', area);
      if (availability) params.set('availability_state', availability);
      if (group) params.set('location_group', group);
      if (query) params.set('q', query);
      const { data } = await axios.get(`/api/v1/warehouse/locations?${params}`);
      setLocations(data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to load locations');
    } finally {
      setLoading(false);
    }
  }, [area, availability, group, page, query]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/v1/warehouse/location-groups?pageSize=200');
      setGroups(data.rows || data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to load location groups');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/v1/warehouse/capacity-profiles?pageSize=200');
      setProfiles(data.rows || data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to load capacity profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCapacityMatrix = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/v1/warehouse/capacity-matrix?pageSize=500');
      setCapacityMatrix(data.rows || data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to load capacity matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChanges = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/v1/warehouse/changes?pageSize=50');
      setChanges(data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to load governed changes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
    loadProfiles();
  }, [loadGroups, loadProfiles]);

  useEffect(() => {
    if (tab === 'locations') loadLocations();
    if (tab === 'groups') loadGroups();
    if (tab === 'capacity-profiles') loadProfiles();
    if (tab === 'capacity-table') loadCapacityMatrix();
    if (tab === 'changes') loadChanges();
  }, [loadCapacityMatrix, loadChanges, loadGroups, loadLocations, loadProfiles, tab]);

  const saved = async () => {
    if (tab === 'locations') await loadLocations();
    if (tab === 'groups') await loadGroups();
    if (tab === 'capacity-profiles') await loadProfiles();
    if (tab === 'capacity-table') await loadCapacityMatrix();
    if (tab === 'changes') await loadChanges();
    onSaved?.();
  };

  const filteredCapacityMatrix = capacityMatrix.filter((row) => (
    `${row.family} ${row.location_scope || ''} ${row.governance_status || ''}`.toLowerCase().includes(capacityQuery.trim().toLowerCase())
  ));
  const totalPages = Math.max(1, Math.ceil(locations.total / 50));

  return (
    <aside className="absolute inset-y-0 right-0 z-40 flex w-full max-w-[720px] flex-col border-l border-gray-200 bg-white shadow-2xl">
      <header className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
        <div><p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Governed review</p><h2 className="mt-0.5 text-lg font-semibold text-gray-900">Location Master</h2><p className="mt-0.5 text-xs text-gray-500">{canEdit ? 'Edits require a reason and are recorded in the change ledger.' : 'Read-only review for your current access.'}</p></div>
        <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><XMarkIcon className="h-5 w-5" /></button>
      </header>
      <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 px-3 pt-2">
        {TABS.map((item) => <button type="button" key={item.key} onClick={() => { setTab(item.key); setPage(1); setExpanded(''); }} className={`whitespace-nowrap rounded-t px-3 py-2 text-xs font-medium ${tab === item.key ? 'border-b-2 border-black text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}>{item.label}</button>)}
      </nav>
      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-4">
        {error && <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        {tab === 'locations' && <section>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <select value={area} onChange={(event) => { setArea(event.target.value); setPage(1); }} className="rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"><option value="">All areas</option>{[...new Set(RUNS.flatMap((run) => run.areas))].map((value) => <option key={value} value={value}>{value}</option>)}</select>
            <select value={availability} onChange={(event) => { setAvailability(event.target.value); setPage(1); }} className="rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"><option value="">Availability</option>{AVAILABILITY.map((value) => <option key={value} value={value}>{value}</option>)}</select>
            <select value={group} onChange={(event) => { setGroup(event.target.value); setPage(1); }} className="rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"><option value="">All groups</option>{groups.map((row) => <option key={row.id || row.name} value={row.name}>{row.name}</option>)}</select>
            <input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="Search code..." className="rounded border border-gray-300 px-2 py-1.5 text-xs" />
          </div>
          <p className="mt-3 text-xs text-gray-500">{locations.total} locations · {Object.entries(locations.summary?.byAvailability || {}).map(([key, value]) => `${key}: ${value}`).join(' · ') || 'No availability summary'}</p>
          <div className="mt-2 overflow-hidden rounded border border-gray-200">
            <table className="w-full text-left text-[11px]"><thead className="bg-gray-50 text-gray-500"><tr><th className="px-2 py-2">Code</th><th className="px-2 py-2">Group</th><th className="px-2 py-2">Availability</th><th className="px-2 py-2">Active</th><th className="px-2 py-2">Pickable</th><th className="px-2 py-2">Capacity</th><th className="px-2 py-2">Governance</th></tr></thead><tbody>{locations.rows.map((row) => <tr key={row.location_code} onClick={() => onSelectLocation(row.location_code)} className="cursor-pointer border-t border-gray-200 hover:bg-gray-50"><td className="px-2 py-2 font-medium text-gray-900">{row.location_code}</td><td className="px-2 py-2 text-gray-600">{row.location_group || '—'}</td><td className="px-2 py-2 text-gray-600">{row.availability_state}</td><td className="px-2 py-2 text-gray-600">{row.active_state}</td><td className="px-2 py-2 text-gray-600">{row.pickable ? 'Yes' : 'No'}</td><td className="px-2 py-2 text-gray-600">{row.capacity_profile_id || '—'}</td><td className="px-2 py-2 text-gray-600">{row.governance_status}</td></tr>)}</tbody></table>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Prev</button><span>Page {page} of {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Next</button></div>
        </section>}
        {tab === 'groups' && <div className="space-y-2">{groups.map((row) => <article key={row.id} className="rounded border border-gray-200 p-3"><button type="button" onClick={() => setExpanded(expanded === `group-${row.id}` ? '' : `group-${row.id}`)} className="flex w-full items-center justify-between text-left"><span><span className="block text-sm font-medium text-gray-900">{row.name}</span><span className="text-xs text-gray-500">{row.group_layer || '—'} · {row.pickable ? 'Pickable' : 'Not pickable'} · {row.storage_form || '—'} · {row.lifecycle_state || '—'} · {row.governance_status || '—'}</span></span><span className="text-xs text-gray-400">{expanded === `group-${row.id}` ? 'Hide' : 'Edit'}</span></button>{expanded === `group-${row.id}` && <GovernedEditForm title="Governed edit" fields={LOCATION_GROUP_FIELDS} record={row} endpoint={`/api/v1/warehouse/location-groups/${encodeURIComponent(row.id)}`} options={options} onSaved={saved} />}</article>)}</div>}
        {tab === 'capacity-profiles' && <div className="space-y-2">{profiles.map((row) => <article key={row.profile_id} className="rounded border border-gray-200 p-3"><button type="button" onClick={() => setExpanded(expanded === `profile-${row.profile_id}` ? '' : `profile-${row.profile_id}`)} className="flex w-full items-center justify-between text-left"><span><span className="block text-sm font-medium text-gray-900">{row.profile_id}</span><span className="text-xs text-gray-500">{row.storage_form || '—'}{row.bin_size ? ` (${row.bin_size} bin)` : ''} · {(row.product_families || []).join(', ') || 'Any'} · {row.governance_status || '—'}</span></span><span className="text-xs text-gray-400">{expanded === `profile-${row.profile_id}` ? 'Hide' : 'Edit'}</span></button><p className="mt-2 text-xs text-gray-600">{row.max_units || '—'} units · {row.max_cartons || '—'} cartons · {row.units_per_carton || '—'} per carton · {row.manual_capacity ? 'manual' : 'governed'}</p>{expanded === `profile-${row.profile_id}` && <GovernedEditForm title="Governed edit" fields={CAPACITY_PROFILE_FIELDS} record={row} options={options} endpoint={`/api/v1/warehouse/capacity-profiles/${encodeURIComponent(row.profile_id)}`} onSaved={saved} />}</article>)}</div>}
        {tab === 'capacity-table' && <section>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <input value={capacityQuery} onChange={(event) => setCapacityQuery(event.target.value)} aria-label="Search capacity families" placeholder="Search family or status..." className="min-w-48 flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs" />
            {capacitySheetSyncAccess.canView && <button type="button" onClick={() => setSyncOpen(true)} className="rounded bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700">Sync from Sheet</button>}
          </div>
          <div className="mt-3 overflow-x-auto rounded border border-gray-200">
            <table className="w-full min-w-[760px] text-left text-[11px]">
              <thead className="bg-gray-50 text-gray-500"><tr>
                <th className="px-2 py-2">Family</th><th className="px-2 py-2">Location scope</th><th className="px-2 py-2">Pallet units</th><th className="px-2 py-2">Small bin units</th><th className="px-2 py-2">Large bin units</th><th className="px-2 py-2">Large pallet units</th><th className="px-2 py-2">Cartons</th><th className="px-2 py-2">Units / carton</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Manual</th>
              </tr></thead>
              <tbody>{filteredCapacityMatrix.map((row) => <tr key={`${row.family}-${row.location_scope || ''}`} className="border-t border-gray-200">
                <td className="px-2 py-2 font-medium text-gray-900">{row.family}</td><td className="px-2 py-2 text-gray-600">{row.location_scope || 'All locations'}</td><td className="px-2 py-2 text-gray-600">{row.pallet_max_units ?? '—'}</td><td className="px-2 py-2 text-gray-600">{row.sm_bin_max_units ?? '—'}</td><td className="px-2 py-2 text-gray-600">{row.lg_bin_max_units ?? '—'}</td><td className="px-2 py-2 text-gray-600">{row.large_pallet_max_units ?? '—'}</td><td className="px-2 py-2 text-gray-600">{row.pallet_max_cartons ?? '—'}</td><td className="px-2 py-2 text-gray-600">{row.units_per_carton ?? '—'}</td><td className="px-2 py-2 text-gray-600">{row.governance_status || '—'}</td><td className="px-2 py-2 text-gray-600">{row.manual_capacity ? 'Yes' : 'No'}</td>
              </tr>)}</tbody>
            </table>
          </div>
          {!filteredCapacityMatrix.length && !loading && <p className="py-6 text-center text-xs text-gray-500">No capacity families found.</p>}
        </section>}
        {tab === 'changes' && <div className="space-y-2">{changes.rows?.map((row) => <article key={row.id || `${row.entity_type}-${row.entity_key}-${row.created_at}`} className="rounded border border-gray-200 p-3"><div className="flex items-center justify-between"><span className="text-xs font-medium text-gray-900">{row.entity_type} · {row.entity_key}</span><span className="text-[11px] text-gray-500">{formatDate(row.created_at)}</span></div><p className="mt-1 text-xs text-gray-600">{row.changed_by} · {row.summary}</p><p className="mt-1 text-xs text-gray-500">{row.reason}</p></article>)}</div>}
        {tab === 'changes' && !changes.rows?.length && <p className="py-6 text-center text-xs text-gray-500">No governed changes yet.</p>}
        {loading && <p className="mt-3 text-xs text-gray-400">Loading...</p>}
      </div>
      <CapacitySheetSyncModal
        isOpen={syncOpen}
        canEdit={capacitySheetSyncAccess.canEdit}
        onClose={() => setSyncOpen(false)}
        onApplied={async () => {
          await loadCapacityMatrix();
          onSaved?.();
        }}
      />
    </aside>
  );
};

export default LocationMasterPanel;
